/**
 * API client for making authenticated requests to the JumpBean API.
 *
 * This module provides utilities for fetching from the API with proper
 * authentication headers from Supabase.
 */

import { supabase } from "./supabase";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * Get the current user's access token from Supabase
 */
async function getAccessToken(): Promise<string | null> {
  if (!supabase) {
    console.warn("Supabase client not available");
    return null;
  }

  const { data: { session }, error } = await supabase.auth.getSession();

  if (error) {
    console.error("Failed to get session:", error);
    return null;
  }

  return session?.access_token || null;
}

/**
 * Create headers for an authenticated API request
 */
async function createAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * API response type
 */
export interface ApiResponse<T> {
  data?: T;
  error?: {
    message: string;
    status: number;
    details?: unknown;
  };
}

/**
 * Make an authenticated GET request to the API
 */
export async function apiGet<T>(endpoint: string): Promise<ApiResponse<T>> {
  try {
    const headers = await createAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "GET",
      headers,
    });

    const json = await response.json();

    if (!response.ok) {
      return {
        error: json.error || {
          message: "Request failed",
          status: response.status,
        },
      };
    }

    // Support both { data: ... } wrapped responses and direct responses
    return { data: json.data ?? json };
  } catch (error) {
    console.error("API GET error:", error);
    return {
      error: {
        message: error instanceof Error ? error.message : "Network error",
        status: 0,
      },
    };
  }
}

/**
 * Make an authenticated POST request to the API
 */
export async function apiPost<T, B = unknown>(
  endpoint: string,
  body: B
): Promise<ApiResponse<T>> {
  try {
    const headers = await createAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    const json = await response.json();

    if (!response.ok) {
      return {
        error: json.error || {
          message: "Request failed",
          status: response.status,
        },
      };
    }

    // Support both { data: ... } wrapped responses and direct responses
    return { data: json.data ?? json };
  } catch (error) {
    console.error("API POST error:", error);
    return {
      error: {
        message: error instanceof Error ? error.message : "Network error",
        status: 0,
      },
    };
  }
}

/**
 * Make an authenticated PATCH request to the API
 */
export async function apiPatch<T, B = unknown>(
  endpoint: string,
  body: B
): Promise<ApiResponse<T>> {
  try {
    const headers = await createAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    });

    const json = await response.json();

    if (!response.ok) {
      return {
        error: json.error || {
          message: "Request failed",
          status: response.status,
        },
      };
    }

    return { data: json.data };
  } catch (error) {
    console.error("API PATCH error:", error);
    return {
      error: {
        message: error instanceof Error ? error.message : "Network error",
        status: 0,
      },
    };
  }
}

/**
 * Make an authenticated DELETE request to the API
 */
export async function apiDelete<T>(endpoint: string): Promise<ApiResponse<T>> {
  try {
    const headers = await createAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "DELETE",
      headers,
    });

    if (response.status === 204) {
      return { data: undefined as T };
    }

    const json = await response.json();

    if (!response.ok) {
      return {
        error: json.error || {
          message: "Request failed",
          status: response.status,
        },
      };
    }

    return { data: json.data };
  } catch (error) {
    console.error("API DELETE error:", error);
    return {
      error: {
        message: error instanceof Error ? error.message : "Network error",
        status: 0,
      },
    };
  }
}
